export class EventEmitter {
	/**
	 * @private
	 * @readonly
	 * @default {}
	 */
	#listeners = {};

	/**
	 * @public
	 * @param {string} eventName
	 * @param {EventCallback} cb
	 * @returns {EventEmitter}
	 */
	on(eventName, cb) {
		if (typeof this.#listeners[eventName] === 'undefined') {
			this.#listeners[eventName] = [];
		}

		this.#listeners[eventName].push(cb);
		return this;
	}

	/**
	 * @public
	 * @param {string} eventName
	 * @param {EventCallback} cb
	 * @returns {EventEmitter}
	 */
	addListener(eventName, cb) {
		return this.on(eventName, cb);
	}

	/**
	 * @public
	 * @param {string} eventName
	 * @param {EventCallback} cb
	 * @returns {EventEmitter}
	 */
	off(eventName, cb) {
		const ls = this.#listeners[eventName];

		if (!ls) {
			return this;
		}

		for (let i = ls.length; i > 0; --i) {
			if (ls[i] === cb) {
				ls.splice(i, 1);
				break;
			}
		}

		return this;
	}

	/**
	 * @public
	 * @param {string} eventName
	 * @param {EventCallback} cb
	 * @returns {EventEmitter}
	 */
	removeListener(eventName, cb) {
		return this.off(eventName, cb);
	}

	/**
	 * @public
	 * @param {string} eventName
	 * @param {EventCallback} cb
	 * @returns {EventEmitter}
	 */
	once(eventName, cb) {
		if (typeof this.#listeners[eventName] === 'undefined') {
			this.#listeners[eventName] = [];
		}

		const onceWrapper = (...args) => {
			cb(...args);
			this.off(eventName, onceWrapper);
		};

		this.#listeners[eventName].push(onceWrapper);
		return this;
	}

	/**
	 * @public
	 * @param {string} eventName
	 * @param {...unknown} [args]
	 * @returns {boolean}
	 */
	emit(eventName, ...args) {
		const fns = this.#listeners[eventName];

		if (!fns) {
			return false;
		}

		fns.forEach((f) => {
			f(...args);
		});
		return true;
	}

	/**
	 * @public
	 * @param {string} eventName
	 * @returns {number}
	 */
	listenerCount(eventName) {
		const fns = this.#listeners[eventName] || [];
		return fns.length;
	}

	/**
	 * @public
	 * @param {string} eventName
	 * @returns {EventCallback[]}
	 */
	rawListeners(eventName) {
		return this.#listeners[eventName];
	}
}
/** @typedef {(...args: unknown[]) => void} EventCallback */
/** @typedef {Object} ListenerMap */
